/**
 * THE MATCH SUGGESTION ON SCREEN (William, 17 Oct 2026, typed in Care's chat):
 *   12:30 AM  W1: a SUGGESTION confirmed by one click, never an automatic match. 12:32 AM: "Also a flag "confirm lookup"".
 *   12:50 AM  "instead of saying verify - I would put a % prob of success ... And, I would then allow for this to be
 *             checked at the main screen and then have a button on top that lights up to do a Match Member".
 *
 * PART R: the pill the row (flat and grouped) and the case top show in place of "Unmatched phone/email", its
 * colour (90 and up green, 70 to 89 amber, under 70 grey), and the "Confirm lookup" filter. PART S2's small rules
 * for the queue's Match Member and Send to Approval Queue buttons live here too, so the bar and its tests read one
 * place. PURE: no fetch, no state.
 *
 * NO em dashes (house voice rule). ASCII hyphens only.
 */
import type { EventRow, RowSuggestion, ThreadRow } from '@lib/care-model';

export type SuggestionTone = 'green' | 'amber' | 'grey';

/** 90+ green, 70-89 amber, under 70 grey (packet R). */
export function suggestionTone(score: number): SuggestionTone {
  if (score >= 90) return 'green';
  if (score >= 70) return 'amber';
  return 'grey';
}

/** The pill's class for a tone (index.css): green = pill-success, amber = pill-warning, grey = the plain pill. */
export const TONE_PILL: Readonly<Record<SuggestionTone, string>> = { green: 'pill pill-success', amber: 'pill pill-warning', grey: 'pill' };

export const SUGGEST_WORDING = {
  /** "Likely: TOMMY F COTTRELL 92% (name, email name)", and "+N other" when there are more. */
  pill: (s: Pick<RowSuggestion, 'fullName' | 'memberId' | 'score' | 'reasons' | 'otherCount'>): string =>
    `Likely: ${s.fullName || `#${s.memberId}`} ${s.score}%${s.reasons.length ? ` (${s.reasons.join(', ')})` : ''}${s.otherCount > 0 ? ` +${s.otherCount} other` : ''}`,
  /** Names the client chosen in the header, whose member list the suggestion came from (William, 17 Oct 2026). */
  pillHelp: (plan: string): string => `A suggestion from ${plan}'s member list, never a match. Confirm it with Match Member; it goes to the Approval Queue.`,
  pending: (name: string) => `Match pending review: ${name}`,
  filterLabel: 'Lookup',
  filter: 'Confirm lookup',
  filterHelp: 'Rows with a suggested member nobody has confirmed yet',
  notThis: 'Not this member',
  notThisHelp: 'Clear the suggestion: this member is not offered for this case again.',
  cleared: 'Suggestion cleared. This member will not be offered for this case again.',
} as const;

/** The row's suggestion while it is unconfirmed: the entry has no member and no match waiting. Else null. */
export function unconfirmed(r: Pick<EventRow, 'memberId' | 'suggestion' | 'matchPending'>): RowSuggestion | null {
  if (r.memberId || r.matchPending) return null;
  return r.suggestion && r.suggestion.memberId ? r.suggestion : null;
}

/** "Confirm lookup": the row has an unconfirmed suggestion. */
export function needsConfirmLookup(r: Pick<EventRow, 'memberId' | 'suggestion' | 'matchPending'>): boolean {
  return !!unconfirmed(r);
}

/**
 * A member's row of the grouped view, read for its suggestion: the newest of its entries (eventIds run newest
 * first) that has an unconfirmed suggestion, or a match waiting. `eventId` names the entry it was read from.
 */
export function threadSuggestion(
  t: Pick<ThreadRow, 'eventIds'>,
  eventsById: ReadonlyMap<string, Pick<EventRow, 'eventId' | 'memberId' | 'suggestion' | 'matchPending'>>,
): { eventId: string; suggestion: RowSuggestion | null; matchPending: EventRow['matchPending'] } | null {
  for (const id of t.eventIds) {
    const r = eventsById.get(id);
    if (!r) continue;
    if (r.matchPending) return { eventId: id, suggestion: r.suggestion ?? null, matchPending: r.matchPending };
    const s = unconfirmed(r);
    if (s) return { eventId: id, suggestion: s, matchPending: null };
  }
  return null;
}

/* ------------------------- THE MATCH COLUMN (William, 17 Oct 2026 about 12:50 AM) -------------------------
 * "We should have the matched flag in its own col. sortable and also so I can isolate them at once". One state per
 * row, in this order: Matched (a member is set), Pending approval (a Find Member match waits), Suggested (an
 * unconfirmed suggestion, with its %), Ambiguous, No match. The sort puts them in that order, Suggested by score
 * high to low; the slicer splits Suggested at 90%.
 */
export type MatchGroup = 'matched' | 'pending' | 'suggested-high' | 'suggested-low' | 'ambiguous' | 'none';

export const MATCH_GROUP_WORDING = {
  column: 'Match',
  slicer: 'Match',
  /** The slicer's buttons. */
  labels: {
    matched: 'Matched',
    pending: 'Pending approval',
    'suggested-high': 'Suggested 90% and over',
    'suggested-low': 'Suggested under 90%',
    ambiguous: 'Ambiguous',
    none: 'No match',
  } as Record<MatchGroup, string>,
  /** The column's words where no pill speaks. */
  matched: 'Matched',
  ambiguous: 'Ambiguous',
  none: 'No match',
} as const;

/** The slicer's order, the column's sort order. */
export const MATCH_GROUP_ORDER: readonly MatchGroup[] = ['matched', 'pending', 'suggested-high', 'suggested-low', 'ambiguous', 'none'];

/** The score that splits the Suggested slicer (the pill's own green line). */
export const SUGGEST_HIGH = 90;

export interface MatchState {
  group: MatchGroup;
  suggestion: RowSuggestion | null;
  pending: EventRow['matchPending'];
}

/** One row's state from its parts: the member, the match waiting, the unconfirmed suggestion, the confidence. */
export function matchStateOf(p: {
  memberId: string | null | undefined;
  pending: EventRow['matchPending'] | undefined;
  suggestion: RowSuggestion | null | undefined;
  matchConfidence: string | null | undefined;
}): MatchState {
  if (p.memberId) return { group: 'matched', suggestion: null, pending: null };
  if (p.pending) return { group: 'pending', suggestion: p.suggestion ?? null, pending: p.pending };
  if (p.suggestion && p.suggestion.memberId) {
    return { group: p.suggestion.score >= SUGGEST_HIGH ? 'suggested-high' : 'suggested-low', suggestion: p.suggestion, pending: null };
  }
  if (p.matchConfidence === 'ambiguous') return { group: 'ambiguous', suggestion: null, pending: null };
  return { group: 'none', suggestion: null, pending: null };
}

/** A flat row's state. */
export function eventMatchState(r: Pick<EventRow, 'memberId' | 'matchPending' | 'suggestion' | 'matchConfidence'>): MatchState {
  return matchStateOf({ memberId: r.memberId, pending: r.matchPending, suggestion: unconfirmed(r), matchConfidence: r.matchConfidence });
}

/**
 * A grouped row's state: a member's row is Matched; a PCP's row is read for nothing but its confidence (as its
 * Member cell has always been); any other row by the newest of its entries with a match waiting or a suggestion.
 */
export function threadMatchState(
  t: Pick<ThreadRow, 'kind' | 'memberId' | 'matchConfidence' | 'eventIds'>,
  eventsById: ReadonlyMap<string, Pick<EventRow, 'eventId' | 'memberId' | 'suggestion' | 'matchPending'>>,
): MatchState {
  if (t.kind === 'member' || t.memberId) return matchStateOf({ memberId: t.memberId || 'member', pending: null, suggestion: null, matchConfidence: t.matchConfidence });
  const hit = t.kind === 'pcp' ? null : threadSuggestion(t, eventsById);
  return matchStateOf({ memberId: '', pending: hit?.matchPending ?? null, suggestion: hit?.suggestion ?? null, matchConfidence: t.matchConfidence });
}

/**
 * The sort value: Matched 1, Pending 2, Suggested 3 (minus the score as a fraction, so a higher score comes first),
 * Ambiguous 4, No match 5. Ascending reads Matched first; the second click reverses it. Never blank, so the grid's
 * "blanks last" rule never moves a row out of its group.
 */
export function matchSortValue(s: MatchState): number {
  switch (s.group) {
    case 'matched': return 1;
    case 'pending': return 2;
    case 'suggested-high':
    case 'suggested-low': return 3 + (100 - Math.max(0, Math.min(100, s.suggestion?.score ?? 0))) / 1000;
    case 'ambiguous': return 4;
    default: return 5;
  }
}
